import {
    Box, Button, Checkbox, Flex, FormControl, FormLabel, Grid, GridItem, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text, Textarea, Tooltip, Radio, RadioGroup, Stack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CustomToast, FormInput } from "../../../../components";
import { Controller, useForm } from "react-hook-form";
import { baseUrl, useFetch } from "../../../../hooks/useFetch";
import { showDateTime } from "../../../../utilities/helper";
import { ViewIcon, AttachmentIcon, DownloadIcon, DeleteIcon } from "@chakra-ui/icons";
import { useAuth } from "../../../../hooks/useAuth";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { refreshHistory } from "../../../../components/History/modal";
import RichTextEditor from "../../../../components/RichEditorText/richEditorText";

interface ISendMessageForm {
    subject: string;
    description: string;
}
interface IAttachment {
    id: number;
    fileName: string;
    relativePath: string;
}
interface IMessageHistory {
    id: number;
    subject: string;
    type: string;
    to: string;
    mailBody: string;
    attachments: IAttachment[];
    extraEmails: string[] | string | null;
    ccExtraEmails: string[] | string | null;
    sentBy: string;
    createdAt: string;
}
interface Props {
    pocName: string;
    pocEmail: string;
    leadId: number;
    viewType?: "table" | "card";
    onMessageSent?: (leadId: number) => void;

}
const schema = yup.object({
    subject: yup.string().trim().required("Subject is required"),
    description: yup.string().trim().required("Mail Body is required").test("not-empty", "Mail Body cannot be empty", (value) => !!value?.trim()),
});

const SendMessageFormV2 = ({ pocName, pocEmail, leadId, onMessageSent }: Props) => {
    const { addToast } = CustomToast();
    const { fetchApi, loading } = useFetch(addToast);
    const [selectedMailBody, setSelectedMailBody] = useState("");
    const [showBodyModal, setShowBodyModal] = useState(false);
    const [showForm, setShowForm] = useState(true);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [messages, setMessages] = useState<IMessageHistory[]>([]);
    const { loginObj } = useAuth();
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);
    const [selectedAttachments, setSelectedAttachments] = useState<IAttachment[]>([]);
    const [emailInput, setEmailInput] = useState("");
    const [emailList, setEmailList] = useState<string[]>(pocEmail ? [pocEmail.toLowerCase()] : []);
    const [emailError, setEmailError] = useState("");
    const [ccEmailInput, setCcEmailInput] = useState("");
    const [ccEmailList, setCcEmailList] = useState<string[]>([]);
    const [ccEmailError, setCcEmailError] = useState("");
    const [includeSignature, setIncludeSignature] = useState(true);
    const [signatureType, setSignatureType] = useState<"Short" | "Full">("Full");
    const [includeDisclaimer, setIncludeDisclaimer] = useState(true);
    const [userSignature, setUserSignature] = useState<{ shortSignature: string; fullSignature: string } | null>(null);
    const [websiteDisclaimer, setWebsiteDisclaimer] = useState<string>("");
    const [loadingSignature, setLoadingSignature] = useState(false);
    const [loadingDisclaimer, setLoadingDisclaimer] = useState(false);

    useEffect(() => {
        const fetchSignature = async () => {
            if (includeSignature && !userSignature && !loadingSignature) {
                setLoadingSignature(true);
                try {
                    const res = await fetchApi<{ shortSignature: string; fullSignature: string }>("UserSignature", "GET");
                    if (res) {
                        setUserSignature({
                            shortSignature: res.shortSignature || "",
                            fullSignature: res.fullSignature || "",
                        });
                    }
                } catch (error) {
                    console.error("Error fetching signature:", error);
                } finally {
                    setLoadingSignature(false);
                }
            }
        };
        fetchSignature();
    }, [includeSignature, userSignature, loadingSignature]);

    useEffect(() => {
        const fetchDisclaimer = async () => {
            if (includeDisclaimer && !websiteDisclaimer && !loadingDisclaimer) {
                setLoadingDisclaimer(true);
                try {
                    const res = await fetchApi<{ disclaimer: string }>("WebsiteSetting", "GET");
                    if (res) {
                        setWebsiteDisclaimer(res.disclaimer || "");
                    }
                } catch (error) {
                    console.error("Error fetching disclaimer:", error);
                } finally {
                    setLoadingDisclaimer(false);
                }
            }
        };
        fetchDisclaimer();
    }, [includeDisclaimer, websiteDisclaimer, loadingDisclaimer]);


    //handle cc email input and list
    const handleCcEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const trimmed = ccEmailInput.trim().replace(/,$/, "").toLowerCase();
            if (!trimmed) return;
            if (!isValidEmail(trimmed)) {
                setCcEmailError("Invalid email address.");
                return;
            }
            if (ccEmailList.includes(trimmed)) {
                setCcEmailError("Email already added.");
                return;
            }
            setCcEmailList(prev => [...prev, trimmed]);
            setCcEmailInput("");
            setCcEmailError("");
        }
    };

    const handleCcEmailBlur = () => {
        const trimmed = ccEmailInput.trim().replace(/,$/, "").toLowerCase();
        if (!trimmed) return;
        if (!isValidEmail(trimmed)) {
            setCcEmailError("Invalid email address.");
            return;
        }
        if (ccEmailList.includes(trimmed)) {
            setCcEmailError("Email already added.");
            return;
        }
        setCcEmailList(prev => [...prev, trimmed]);
        setCcEmailInput("");
        setCcEmailError("");
    };

    const removeCcEmail = (email: string) =>
        setCcEmailList(prev => prev.filter(e => e !== email));


    const isValidEmail = (email: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
            if (!trimmed) return;
            if (!isValidEmail(trimmed)) { setEmailError("Invalid email address."); return; }
            if (emailList.includes(trimmed)) { setEmailError("Email already added."); return; }
            setEmailList(prev => [...prev, trimmed]);
            setEmailInput("");
            setEmailError("");
        }
    };

    const handleEmailBlur = () => {
        const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
        if (!trimmed) return;
        if (!isValidEmail(trimmed)) { setEmailError("Invalid email address."); return; }
        if (emailList.includes(trimmed)) { setEmailError("Email already added."); return; }
        setEmailList(prev => [...prev, trimmed]);
        setEmailInput("");
        setEmailError("");
    };

    const removeEmail = (email: string) =>
        setEmailList(prev => prev.filter(e => e !== email));

    const { control, handleSubmit, reset, register, formState: { errors },
    } = useForm<ISendMessageForm>({
        resolver: yupResolver(schema),
        defaultValues: {
            subject: "", description: "",
        },
    });

    const loadMessages = async () => {
        const res = await fetchApi<IMessageHistory[]>(`LeadRequirement/GetMessageHistory?leadId=${leadId}`, "GET");
        if (Array.isArray(res)) {
            setMessages(res);
        } else {
            setMessages([]);
        }
    };

    useEffect(() => {
        if (leadId) {
            loadMessages();
        }
    }, [leadId]);

    useEffect(() => {
        if (pocEmail) {
            setEmailList([pocEmail.toLowerCase()]);
        } else {
            setEmailList([]);
        }
    }, [pocEmail]);

    const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm", ".wmv"];
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];

    const MAX_IMAGE_DOC_SIZE = 5 * 1024 * 1024;
    const MAX_VIDEO_SIZE = 20 * 1024 * 1024;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const files = Array.from(e.target.files);
        const validFiles: File[] = [];
        const errorsList: string[] = [];

        files.forEach((file) => {
            const ext = "." + file.name.split(".").pop()?.toLowerCase();
            const isVideo = videoExtensions.includes(ext);
            const isImage = imageExtensions.includes(ext);
            const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_DOC_SIZE;

            if (file.size > maxSize) {
                const limitText = isVideo ? "20 MB" : "5 MB";
                const typeLabel = isVideo ? "Video" : isImage ? "Image" : "File";
                errorsList.push(`${typeLabel} '${file.name}' exceeds ${limitText} limit.`);
            } else {
                validFiles.push(file);
            }
        });

        if (errorsList.length > 0) {
            addToast({ message: errorsList.join(" "), status: "error" });
        }
        setAttachments((prev) => [...prev, ...validFiles]);
    };

    const onSubmit = async (data: ISendMessageForm) => {
        let finalEmails = [...emailList];
        const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
        if (trimmed) {
            if (isValidEmail(trimmed)) {
                if (!finalEmails.includes(trimmed)) {
                    finalEmails.push(trimmed);
                    setEmailList(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
                    setEmailInput("");
                    setEmailError("");
                }
            } else {
                setEmailError("Invalid email address.");
                return;
            }
        }

        if (finalEmails.length === 0) {
            setEmailError("Please add at least one recipient email.");
            return;
        }

        //for cc emails
        let finalCcEmails = [...ccEmailList];

        const trimmedCc = ccEmailInput.trim().replace(/,$/, "").toLowerCase();

        if (trimmedCc) {
            if (isValidEmail(trimmedCc)) {
                if (!finalCcEmails.includes(trimmedCc)) {
                    finalCcEmails.push(trimmedCc);
                }
            } else {
                setCcEmailError("Invalid email address.");
                return;
            }
        }
        const formData = new FormData();
        formData.append("SenderName", pocName);
        formData.append("SenderEmail", pocEmail);
        formData.append("Subject", data.subject);
        formData.append("Title", data.subject);
        formData.append("Body", data.description);
        formData.append("EmailTo", pocEmail);
        formData.append("LeadId", leadId.toString());
        formData.append("IncludeSignature", includeSignature.toString());
        formData.append("IncludeDisclaimer", includeDisclaimer.toString());
        formData.append("SignatureType", signatureType);
        finalEmails.forEach((email) => {
            formData.append("ExtraEmails", email);
        });
        finalCcEmails.forEach(email => {
            formData.append("CCExtraEmails", email);
        });
        attachments.forEach((file) => {
            formData.append("Attachments", file);
        });
        const res = await fetchApi("LeadRequirement/send", "POST", formData, null, "Email Sent Successfully", true);
        if (res) {
            reset();
            setAttachments([]);
            setEmailList(pocEmail ? [pocEmail.toLowerCase()] : []);
            setEmailInput("");
            setEmailError("");
            setCcEmailList([]);
            setCcEmailInput("");
            setCcEmailError("");
            setIncludeSignature(false);
            setIncludeDisclaimer(false);
            setSignatureType("Full");
            onMessageSent?.(leadId);
            loadMessages();
            refreshHistory("Lead", leadId);
        }
    };

   

    const downloadAttachment = async (attachmentId: number, fileName: string) => {
        const response = await fetch(
            `${baseUrl}LeadRequirement/download-attachment/${attachmentId}`,
            { headers: { Authorization: `Bearer ${loginObj?.token}`, }, });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    };

   

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={4}>
                <Text className="font-poppins text_lg text_semibold">
                    Send Email
                </Text>
            </Flex>

            {showForm && (
                <Box mb={6}  p={5} bg="white" border="1px solid #EAECF0" borderRadius="12px" boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)" >
                    <Flex justify="space-between" align="center" mb={4} pb={3} borderBottom="1px solid #F2F4F7">
                        <Text fontSize="md" fontWeight="600" color="#101828">
                            Compose Email
                        </Text>
                    </Flex>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid templateColumns="1fr" gap={4}>
                            {/* Row 1: To & Subject */}
                            <GridItem colSpan={2}>
                                <FormControl isInvalid={!!emailError}>
                                    <FormLabel className="font-poppins text_medium text_md" color="#344054" mb={2}>To</FormLabel>
                                    {emailList.length > 0 && (
                                        <Flex flexWrap="wrap" gap={1.5} mb={2.5} p={2} border="1px solid" borderColor="#EAECF0" borderRadius="8px" bg="#F9FAFB">
                                            {emailList.map(email => (
                                                <Flex key={email} align="center" gap={1} bg="#EEF4FF" color="#3538CD" borderRadius="6px" px={2.5} py={0.75} fontSize="xs" fontWeight="500" border="1px solid" borderColor="#C7D7FE">
                                                    <Text>{email}</Text>
                                                    <Text cursor="pointer" fontWeight="bold" ml={1.5} onClick={() => removeEmail(email)} _hover={{ color: "#B93815" }}>×</Text>
                                                </Flex>
                                            ))}
                                        </Flex>
                                    )}
                                    <Input
                                        placeholder="Type email and press Enter or , to add"
                                        value={emailInput}
                                        onChange={e => { setEmailInput(e.target.value); setEmailError(""); }}
                                        onKeyDown={handleEmailKeyDown} onBlur={handleEmailBlur} bg="white"
                                        borderColor="#D0D5DD"
                                        _focus={{ borderColor: "#4048D6", boxShadow: "0 0 0 1px #4048D6" }} 
                                        borderRadius="8px"
                                        size="md"
                                    />
                                    {emailError && <Text color="red.500" fontSize="sm" mt={1}>{emailError}</Text>}
                                </FormControl>
                            </GridItem>
                            <GridItem colSpan={2}>
                                <FormControl isInvalid={!!ccEmailError}>
                                    <FormLabel className="font-poppins text_medium text_md">
                                        CC
                                    </FormLabel>

                                    {ccEmailList.length > 0 && (
                                        <Flex flexWrap="wrap" gap={2} mb={2} p={2} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
                                            {ccEmailList.map(email => (
                                                <Flex key={email} align="center" gap={1} bg="#EEF0FD" color="#4048D6" borderRadius="full" px={3} py={1} fontSize="sm" fontWeight="500">
                                                    <Text>{email}</Text>
                                                    <Text cursor="pointer" fontWeight="bold" ml={1} onClick={() => removeCcEmail(email)} _hover={{ color: "red.500" }}>
                                                        ×
                                                    </Text>
                                                </Flex>
                                            ))}
                                        </Flex>
                                    )}

                                    <Input
                                        placeholder="Type email and press Enter or , to add"
                                        value={ccEmailInput}
                                        onChange={(e) => {
                                            setCcEmailInput(e.target.value);
                                            setCcEmailError("");
                                        }}
                                        onKeyDown={handleCcEmailKeyDown} onBlur={handleCcEmailBlur}
                                    />

                                    {ccEmailError && ( 
                                        <Text color="red.500" fontSize="sm" mt={1}> {ccEmailError} </Text>
                                    )}

                                    <Text color="gray.400" fontSize="xs" mt={1}>
                                        Press <strong>Enter</strong> or <strong>,</strong> to add multiple emails
                                    </Text>
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={1}>
                                <FormInput name="subject" control={control} label="Subject" isRequired errors={errors} />
                            </GridItem>

                            {/* Row 2: Attachments & Mail Body */}


                            <GridItem colSpan={2}>
                                <FormControl isInvalid={!!errors.description}>
                                    <FormLabel>
                                        Mail Body <Text as="span" color="red.500">*</Text>
                                    </FormLabel>

                                    <Controller 
                                        control={control}
                                        name="description"
                                        render={({ field }) => (
                                            <RichTextEditor
                                                value={field.value ?? "Hello"}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />

                                    {errors.description && (
                                        <Text color="red.500"> {errors.description.message} </Text>
                                    )}
                                </FormControl>
                            </GridItem>

                       </Grid>
                        <FormControl>
                            <FormLabel>Email Options</FormLabel>

                            <Flex direction="column" gap={4}>
                                <Box>
                                    <Checkbox isChecked={includeSignature} onChange={(e) => setIncludeSignature(e.target.checked)} >
                                        Show Signature
                                    </Checkbox>
                                    {includeSignature && (
                                        <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                            <RadioGroup onChange={(val) => setSignatureType(val as "Short" | "Full")} value={signatureType}>
                                                <Flex gap={4} mb={2}>
                                                    <Radio value="Short" colorScheme="blue">Short Signature</Radio>
                                                    <Radio value="Full" colorScheme="blue">Full Signature</Radio>
                                                </Flex>
                                            </RadioGroup>
                                            {loadingSignature ? (
                                                <Flex align="center" gap={2} mt={2}>
                                                    <Spinner size="xs" color="#4048D6" />
                                                    <Text fontSize="xs" color="gray.500">Loading signature...</Text>
                                                </Flex>
                                            ) : (
                                                <Box mt={2} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" maxH="150px" overflowY="auto" fontSize="sm" >
                                                    {signatureType === "Short" ? (
                                                        userSignature?.shortSignature ? (
                                                            <Box dangerouslySetInnerHTML={{ __html: userSignature.shortSignature }} />
                                                        ) : (
                                                            <Text color="gray.400" fontStyle="italic">No short signature configured.</Text>
                                                        )
                                                    ) : (
                                                        userSignature?.fullSignature ? (
                                                            <Box dangerouslySetInnerHTML={{ __html: userSignature.fullSignature }} />
                                                        ) : (
                                                            <Text color="gray.400" fontStyle="italic">No full signature configured.</Text>
                                                        )
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                <Box>
                                    <Checkbox isChecked={includeDisclaimer} onChange={(e) => setIncludeDisclaimer(e.target.checked)} >
                                        Disclaimer
                                    </Checkbox>
                                    {includeDisclaimer && (
                                        <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                            {loadingDisclaimer ? (
                                                <Flex align="center" gap={2}>
                                                    <Spinner size="xs" color="#4048D6" />
                                                    <Text fontSize="xs" color="gray.500">Loading disclaimer...</Text>
                                                </Flex>
                                            ) : (
                                                <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" maxH="150px" overflowY="auto" fontSize="sm">
                                                    {websiteDisclaimer ? (
                                                        <Box dangerouslySetInnerHTML={{ __html: websiteDisclaimer }} />
                                                    ) : (
                                                        <Text color="gray.400" fontStyle="italic">No disclaimer configured.</Text>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Flex>
                        </FormControl>

                        <Grid
                            templateColumns={{ base: "repeat(1,1fr)", md: "repeat(3,1fr)", }} gap={3} mt={3} >
                            {attachments.map((file, index) => (
                                <Box key={index} border="1px solid #EAECF0" borderRadius="4px" bg="#F9FAFB" p={1} alignContent="center">
                                    <Flex justify="space-between" align="flex-start" mb={2}>
                                        <Text noOfLines={2} fontSize="sm" fontWeight="500" color="#344054" flex="1" mr={2}>
                                            {file.name}
                                        </Text>

                                        <IconButton aria-label="Delete attachment" icon={<DeleteIcon />} size="xs" variant="ghost" color="red.500" _hover={{ bg: "#FEE2E2" }}
                                            onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== index))}
                                        />
                                    </Flex>
                                </Box>
                            ))}
                        </Grid>

                        <Flex justify="flex-end" align="center" gap={3} mt={6} pt={4} borderTop="1px solid #F2F4F7">
                            <Box>
                                <Input id="attachment-upload" type="file" multiple accept=".pdf,.png,.jpg,.jpeg" display="none" onChange={handleFileChange} />

                                <Tooltip label="Attach Files" hasArrow placement="top">
                                    <IconButton as="label" htmlFor="attachment-upload" aria-label="Attach Files" icon={<AttachmentIcon />} variant="ghost" size="md" color="#4048D6" bg="#EEF4FF" borderRadius="8px" cursor="pointer"
                                        _hover={{ bg: "#DCE8FF", color: "#3538CD", }}
                                    />
                                </Tooltip>
                            </Box>

                            <Button bg="#4048D6" color="white" size="sm" h="40px" borderRadius="8px" type="submit" isLoading={loading} _hover={{ bg: "#353CC4" }}>
                                Send
                            </Button>
                        </Flex>
                    </form>
                </Box>
            )}

            {/* Email Log Cards */}
            <Box>
                {loading ? (
                    <Flex justify="center" align="center" height="300px">
                        <Spinner size="lg" color="#4048D6" />
                    </Flex>
                ) : messages.length === 0 ? (
                    <Flex direction="column" justify="center" align="center" height="300px">
                        <Text color="gray.500" fontWeight="500">No Messages Found</Text>
                    </Flex>
                ) : (
                    <Grid gap={4}>
                        {messages.map((message) => (
                            <Box key={message.id} bg="white" border="1px solid #EAECF0" borderRadius="12px" p={5}
                                shadow="sm" transition="all 0.2s" _hover={{ shadow: "md", borderColor: "#D0D5DD" }} >
                                <Flex justify="space-between" align="start" mb={4}>
                                    <Box>
                                        <Text fontSize="lg" fontWeight="600" color="#101828" mb={1}>
                                            {message.subject}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            Sent by {message.sentBy}
                                        </Text>
                                    </Box>
                                    <Flex gap={2}>
                                        <IconButton
                                            aria-label="View Mail Body"
                                            icon={<ViewIcon />} size="sm" variant="ghost" color="#667085" _hover={{ color: "#4048D6", bg: "#F9FAFB" }}
                                            onClick={() => {
                                                setSelectedMailBody(message.mailBody);
                                                setShowBodyModal(true);
                                            }}
                                        />
                                        {message.attachments?.length > 0 && (
                                            <IconButton
                                                aria-label="Show Attachments"
                                                icon={<AttachmentIcon />}
                                                size="sm" variant="ghost" color="#667085" _hover={{ color: "#4048D6", bg: "#F9FAFB" }}
                                                onClick={() => {
                                                    setSelectedAttachments(message.attachments);
                                                    setShowAttachmentModal(true);
                                                }}
                                            />
                                        )}
                                    </Flex>
                                </Flex>

                                <Grid templateColumns={{ base: "repeat(2,1fr)", md: "repeat(3,1fr)", }} gap={4} bg="#F9FAFB" p={3.5} borderRadius="8px"
                                    border="1px solid"
                                    borderColor="#F2F4F7"
                                >
                                    <Box>
                                        <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Type</Text>
                                        <Text fontWeight="600" color="#344054" fontSize="sm">{message.type}</Text>
                                    </Box>
                                    <Box>
                                        <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Sent At</Text>
                                        <Text fontWeight="600" color="#344054" fontSize="sm">{showDateTime(message.createdAt)}</Text>
                                    </Box>
                                    <Box>
                                        <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>
                                            Sent To
                                        </Text>

                                        {(() => {
                                            const toEmails: string[] =
                                                Array.isArray(message.extraEmails) ? message.extraEmails
                                                    : typeof message.extraEmails === "string" && message.extraEmails
                                                        ? message.extraEmails.split(",").map((e: string) => e.trim())
                                                        : [];

                                            const ccEmails: string[] =
                                                Array.isArray(message.ccExtraEmails) ? message.ccExtraEmails
                                                    : typeof message.ccExtraEmails === "string" && message.ccExtraEmails
                                                        ? message.ccExtraEmails.split(",").map((e: string) => e.trim())
                                                        : [];

                                            const allEmails = [...toEmails, ...ccEmails];

                                            return allEmails.length ? (
                                                <Tooltip
                                                    label={
                                                        <Flex direction="column" gap={2} p={1}>
                                                            {toEmails.length > 0 && (
                                                                <>
                                                                    <Text fontWeight="bold" fontSize="sm"> To </Text>
                                                                    {toEmails.map((email, index) => (
                                                                        <Text key={`to-${index}`} fontSize="sm">
                                                                            {email}
                                                                        </Text>
                                                                    ))}
                                                                </>
                                                            )}

                                                            {ccEmails.length > 0 && (
                                                                <>
                                                                    <Text fontWeight="bold" fontSize="sm" mt={1}> CC </Text>
                                                                    {ccEmails.map((email, index) => (
                                                                        <Text key={`cc-${index}`} fontSize="sm">
                                                                            {email}
                                                                        </Text>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </Flex>
                                                    }
                                                    hasArrow placement="top" bg="white" color="black" border="1px solid" borderColor="gray.200" borderRadius="md" >
                                                    <Text fontWeight="600" color="#344054" fontSize="sm" isTruncated maxW="180px" cursor="pointer" >
                                                        {allEmails.join(", ")}
                                                    </Text>
                                                </Tooltip>
                                            ) : (
                                                <Text>-</Text>
                                            );
                                        })()}
                                    </Box>
                                </Grid>
                            </Box>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* Mail Body View Modal */}
            <Modal isOpen={showBodyModal} onClose={() => setShowBodyModal(false)} size="4xl">
                <ModalOverlay />
                <ModalContent borderRadius="12px">
                    <ModalHeader borderBottom="1px solid #EAECF0">Mail Body</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody py={5}>
                        <Box dangerouslySetInnerHTML={{ __html: selectedMailBody }} style={{ whiteSpace: "pre-wrap" }} />
                    </ModalBody>
                    <ModalFooter borderTop="1px solid #EAECF0">
                        <Button variant="outline" size="sm" borderRadius="8px" onClick={() => setShowBodyModal(false)}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Attachments List Modal */}
            <Modal isOpen={showAttachmentModal} onClose={() => setShowAttachmentModal(false)} size="lg">
                <ModalOverlay />
                <ModalContent borderRadius="12px">
                    <ModalHeader borderBottom="1px solid #EAECF0">Attachments</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody py={5}>
                        <Flex direction="column" gap={3}>
                            {selectedAttachments.length > 0 ? (
                                selectedAttachments.map((file) => (
                                    <Flex key={file.id} align="center" justify="space-between" p={3} border="1px solid #E2E8F0" borderRadius="8px" bg="#F9FAFB">
                                        <Flex align="center" gap={2}>
                                            <AttachmentIcon color="#4048D6" />
                                            <Text noOfLines={1} fontSize="sm" color="#344054" fontWeight="500">{file.fileName}</Text>
                                        </Flex>
                                        <IconButton
                                            aria-label="Download Attachment"
                                            icon={<DownloadIcon />}
                                            size="sm"
                                            variant="ghost"
                                            color="#4048D6"
                                            onClick={() => downloadAttachment(file.id, file.fileName)}
                                        />
                                    </Flex>
                                ))
                            ) : (
                                <Text color="gray.500">No attachments found</Text>
                            )}
                        </Flex>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid #EAECF0">
                        <Button variant="outline" size="sm" borderRadius="8px" onClick={() => setShowAttachmentModal(false)}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default SendMessageFormV2;
